import { toast } from "sonner";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";

interface PersonaDeleteDialogProps {
  projectId: string;
  persona: { id: string; name: string } | null;
  onClose: () => void;
}

export function PersonaDeleteDialog({
  projectId,
  persona,
  onClose,
}: PersonaDeleteDialogProps) {
  const utils = trpc.useUtils();

  const deleteMutation = trpc.persona.delete.useMutation({
    onSuccess: (result) => {
      toast.success(result.archived ? "Persona archived" : "Persona deleted");
      utils.persona.list.invalidate({ projectId });
      onClose();
    },
    onError: (err) => toastTrpcError(err, "Failed to delete persona"),
  });

  return (
    <Dialog
      open={persona !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete &ldquo;{persona?.name}&rdquo;?</DialogTitle>
          <DialogDescription>
            &ldquo;{persona?.name}&rdquo; will be deleted. If it has been used
            in walkthroughs, it will be archived instead.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => {
              if (persona) deleteMutation.mutate({ id: persona.id });
            }}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? "Deleting..." : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
